import { Component, inject, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaVessel } from './vessel.model';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { ToastrService } from 'ngx-toastr';
import { MaClient } from './client.model';
import { ClientService } from 'src/app/shared/service/disbursment/client.service';
import { allowLimitedInput, allowOnlyLetters, formatAddressForDisplay, formatAddressForStorage, handlePasteText, trimInput } from '../../master.utils';
import { ENTER } from '@angular/cdk/keycodes';
import { mailRegex } from 'src/app/shared/constants/mailRegex.constants';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

@Component({
  selector: 'app-create-client',
  templateUrl: './create-client.component.html',
  styleUrls: ['./create-client.component.scss']
})
export class CreateClientComponent {
 clientForm: FormGroup;
  isEditMode = false;
  vesselData: MaVessel[] = [];
  filteredVessels: MaVessel[] = [];
  selectedVessels: MaVessel[] = [];
  vesselList: MaVessel[] = [];
  vesselSearchText: string = '';
 private loader = inject(DisbursmentService);
 isLoading$ = this.loader.loading$;
  vslpayload: any
  disallowNumericData = allowOnlyLetters;
  allowLimitedInput = allowLimitedInput;
  emailList: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER];
  removable = true;
  addOnBlur = true;
  private clientClass: MaClient = new MaClient();
  constructor(private fb: FormBuilder, @Inject(MAT_DIALOG_DATA) public existingdata: any,
    private dialogRef: MatDialogRef<CreateClientComponent>,
    private clientService: ClientService, private snackBar: ToastrService,private requestFormService: RequestFormService) {
    this.isEditMode = !!existingdata;
    const data = existingdata?.rowData || {};
    this.clientForm = this.fb.group({
      company_id: [data?.company_id || null], //Added Company_Id by Rahul
      company_name: [data?.company_name || '', Validators.required],
      email: [],
      vsl_ids: [data?.vsl_ids || [], Validators.required],
      vesselSearch: [''],
      address: [formatAddressForDisplay(data?.address || ''), Validators.required], // Added the client Address by Rahul
    })
    if (this.isEditMode) {
      this.vslpayload = {
        company_id: data.company_id,
        fields: ["assigned_unassigned"]
      }
    } else {
      this.vslpayload = {
        company_id: null,
        fields: ["assigned_unassigned"]
      }
    }

    if (data?.email) {
      if (typeof data.email === 'string') {
        this.emailList = data.email.split(',').map((e: string) => e.trim()).filter(Boolean);
      } else if (Array.isArray(data.email)) {
        this.emailList = data.email.map((e: string) => e.trim()).filter(Boolean);
      } else {
        this.emailList = [];
      }
    }

    // Setup vessel search functionality
    this.clientForm.get('vesselSearch')?.valueChanges.subscribe(value => {
      if (typeof value === 'string') {
        this.vesselSearchText = value;
        this.filterVessels();
      }
    });
  }
  ngOnInit() {
    this.getVesselDetailsFromDb();
  }

  filterVessels(): void {
    if (!this.vesselSearchText) {
      this.filteredVessels = [...this.vesselList];
    } else {
      this.filteredVessels = this.vesselList.filter(vessel => 
        vessel.name?.toLowerCase().includes(this.vesselSearchText.toLowerCase()) 
      );
    }
  }

  onVesselChange(vessel: MaVessel): void {
    this.updateFormControl();
  }

  toggleVessel(vessel: MaVessel): void {
    const index = this.selectedVessels.findIndex(v => v.vessel_id === vessel.vessel_id);
    if (index > -1) {
      this.selectedVessels.splice(index, 1);
    } else {
      this.selectedVessels.push(vessel);
    }
    this.updateFormControl();
    this.updateSearchField();
  }

  toggleSelectAll(): void {
    const selectAll = !this.isAllSelected;
    this.filteredVessels.forEach((vessel: MaVessel) => {
      vessel.selected = selectAll;
    });
    this.updateFormControl();
  }

  isVesselSelected(vessel: MaVessel): boolean {
    return this.selectedVessels.some(v => v.vessel_id === vessel.vessel_id);
  }

  get isAllSelected(): boolean {
    return this.filteredVessels.length > 0 && this.filteredVessels.every((vessel: MaVessel) => vessel.selected);
  }

  get isIndeterminate(): boolean {
    const selectedCount = this.filteredVessels.filter((vessel: MaVessel) => vessel.selected).length;
    return selectedCount > 0 && selectedCount < this.filteredVessels.length;
  }

  displayVessel(vessel: MaVessel): string {
    return vessel ? vessel.name || '' : '';
  }

  updateFormControl(): void {
    const selectedVessels = this.filteredVessels.filter((v: MaVessel) => v.selected);
    const vesselIds = selectedVessels.map((v: MaVessel) => v.vessel_id);
    this.clientForm.get('vsl_ids')?.setValue(vesselIds);
  }

  updateSearchField(): void {
    const selectedNames = this.selectedVessels.map((v: MaVessel) => v.name).join(', ');
    this.clientForm.get('vesselSearch')?.setValue(selectedNames, { emitEvent: false });
  }

  //method to trim the input
  onInputTrim(controlName: string): void {
    const control = this.clientForm.get(controlName);
    trimInput(control);
  }


  onPaste(event: ClipboardEvent): void {
    const value = handlePasteText(event);
    this.clientForm.get('company_name')?.setValue(value);
  }

  // method to create the vessel when the add client button is clicked
  onSubmit() {
    if (this.clientForm.valid) {
      this.loader.showDialogLoader();
      const formValue = {
        ...this.clientForm.value,
        company_name: this.clientForm.value.company_name.trim(),
        email: this.emailList,
        status: this.clientClass.status,
        address: formatAddressForStorage(this.clientForm.value.address.trim())
      };
      this.clientService.ClientDataToDB(formValue).subscribe({
        next: (res) => {
          this.loader.hideDialogLoader();
          this.snackBar.success(res.message);
          this.dialogRef.close('refresh');
        },
        error: (err) => {
          this.loader.hideDialogLoader();
          const errmsg = err?.error?.error || "something went wrong"
          this.snackBar.error(errmsg);
        }
      })
    }
    else{
      this.snackBar.error("please fill all the required fields")
    }
    }
    
  // onPaste it will take only 50 characters
  limitPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const limitedText = pastedText.slice(0, 49);

    const input = event.target as HTMLInputElement;
    input.value = limitedText.trim();
  }

  getVesselDetailsFromDb() {
    this.loader.showDialogLoader();
    this.clientService.VslListFromDb(this.vslpayload).subscribe({
      next: (response) => {
        if (this.isEditMode) {
          const mergedArr = [...response.assigned_vessels, ...response.unassigned_vessels];
          this.vesselList = mergedArr.map((v: any) => ({ ...v, selected: false }));
          // Set selected vessels for edit mode
          const selectedIds = this.clientForm.get('vsl_ids')?.value || [];
          this.vesselList.forEach((v: MaVessel) => {
            v.selected = selectedIds.includes(v.vessel_id);
          });
        } else {
          this.vesselList = response.unassigned_vessels.map((v: any) => ({ ...v, selected: false }));
        }
        this.filteredVessels = [...this.vesselList];
        this.loader.hideDialogLoader();
      }
    })
  }

  // method to close the dialog
  closeDialog() {
    this.dialogRef.close();
  }

  // Method to handle chip input: validate and add email to emailList, update form control
  addEmail(event: MatChipInputEvent): void {
    const input = event.input;
    const value = (event.value || '').trim();

    if (value && this.isValidEmail(value)) {
      this.emailList.push(value);
      if (this.emailList.length > 0) {
        input.value = '';
      }
    } else if (value) {
      this.snackBar.error('Invalid email format.');
    }

    // if (input) {
    //   input.value = '';
    // }
  }

  // Method to remove the given email from emailList, update form control value,
  removeEmail(email: string): void {
    const index = this.emailList.indexOf(email);
    if (index >= 0) {
      this.emailList.splice(index, 1);
    }
  }

  // method to validate the email
  isValidEmail(email: string): boolean {
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{3,}$/;
    // return emailRegex.test(email);
    return mailRegex.test(email);
  }
  // add email to chip on typing of valid email
  checkAndAutoAddEmail(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (this.isValidEmail(value)) {
      if (!this.emailList.includes(value)) {
        this.emailList.push(value);
        this.clientForm.get('email')?.setErrors(null);
        input.value = '';
      } else {
        this.clientForm.get('email')?.setErrors({ customEmailError: true });
        input.value = '';
      }
    }
  };
  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }
}
